import type { TLineItemPriceCardFormState } from '@/ui/components/addEditItemSidebar/fields/LineItemPriceCard';
import {
  calculateMarkupChange,
  calculatePredictedMaterialCosts,
} from '@/ui/components/addEditItemSidebar/utils';
import type { TFormState } from '@/ui/components/addEditProposalLineItem/formState';
import type { MaterialState } from '@/ui/components/buildMaterialsListSidebar/BuildMaterialsListSidebar';

type EditorMaterial = Pick<
  MaterialState,
  'id' | 'name' | 'image' | 'suppliedBy' | 'tags'
>;

/**
 * The subset of a saved Line Item the editor's defaults are derived from,
 * defined structurally so both a Proposal line item and an Estimate line item
 * (separate generated GraphQL types) satisfy it. Estimates omit the
 * `selection` and `thumbnail` fields, which the editor defaults/hides.
 */
export type LineItemEditorData = {
  internalReference?: string | null;
  description?: string | null;
  quantity: number;
  selection?: { type: TFormState['selectionType'] } | null;
  thumbnail?: { id: string; url: string } | null;
  price: {
    unitPriceExcTax: { value: number };
    markupAmount?: { value: number } | null;
    markupPercentage?: number | null;
    taxRate?: { id: string } | null;
    cisDeductionRate?: number | null;
    discount?: { percentage: number; description?: string | null } | null;
  };
  materials: Array<{
    perUnitMaterialQuantity: number;
    material: EditorMaterial;
  }>;
  lineItem?: { id: string } | null;
};

/**
 * Builds the Line Item editor's form defaults from a saved Line Item, deriving
 * the markup representation (profit %, profit amount, or fixed price) the item
 * was priced with. Pure and kept apart from `formState`'s query-bound hooks so
 * the markup-derivation maths is unit-testable in isolation.
 *
 * A Line Item with no tax rate (e.g. an Estimate line whose catalogue Item had
 * none) falls back to the account default, so the required-tax-rate form stays
 * valid and autosave is not blocked.
 */
export const buildDefaultValues = (
  proposalLineItemData: LineItemEditorData,
  defaultTaxRateId?: string
): TFormState => {
  const predictedMaterialCosts = calculatePredictedMaterialCosts(
    proposalLineItemData.materials.map((material) => ({
      materialQuantity: material.perUnitMaterialQuantity,
      // The backend orders the suppliers by 'preferred supplier' or lowest price
      price: material.material.suppliedBy[0]?.price.unitPriceExcTax.value || 0,
    }))
  );

  let markupTotals: TLineItemPriceCardFormState['markupTotals'];
  if (typeof proposalLineItemData.price?.markupPercentage === 'number') {
    const profitPercentage =
      Math.round(
        (proposalLineItemData.price.markupPercentage /
          (100 + proposalLineItemData.price.markupPercentage)) *
          100 *
          100000
      ) / 100000;

    markupTotals = {
      markupType: 'profitPercentage',
      ...calculateMarkupChange(
        'profitPercentage',
        profitPercentage,
        predictedMaterialCosts
      ),
    };
  } else if (proposalLineItemData.price?.markupAmount) {
    markupTotals = {
      markupType: 'profitAmount',
      ...calculateMarkupChange(
        'profitAmount',
        proposalLineItemData.price.markupAmount.value,
        predictedMaterialCosts
      ),
    };
  } else {
    markupTotals = {
      markupType: 'price',
      ...calculateMarkupChange(
        'price',
        proposalLineItemData.price?.unitPriceExcTax.value || 0,
        predictedMaterialCosts
      ),
    };
  }

  return {
    thumbnail: proposalLineItemData.thumbnail
      ? {
          id: proposalLineItemData.thumbnail.id,
          file: undefined,
          preview: proposalLineItemData.thumbnail.url,
        }
      : undefined,
    reference: proposalLineItemData.internalReference || '',
    description: proposalLineItemData.description ?? '',
    quantity: proposalLineItemData.quantity,
    selectionType: proposalLineItemData.selection?.type ?? 'REQUIRED',
    taxRateId: proposalLineItemData.price.taxRate?.id || defaultTaxRateId || '',
    cis:
      typeof proposalLineItemData.price.cisDeductionRate === 'number'
        ? ('yes' as const)
        : ('no' as const),
    predictedMaterialCost: predictedMaterialCosts,
    markupTotals,
    autoUpdatePrice: true,
    hasDiscount: !!proposalLineItemData.price.discount,
    discountPercentage:
      proposalLineItemData.price.discount?.percentage || undefined,
    discountDescription:
      proposalLineItemData.price.discount?.description || undefined,
    materials: proposalLineItemData.materials.map((i) => ({
      id: i.material.id,
      name: i.material.name,
      image: i.material.image,
      suppliedBy: i.material.suppliedBy,
      quantity: i.perUnitMaterialQuantity,
      tags: i.material.tags,
    })),
    canUpdateOriginalLineItem: !!proposalLineItemData.lineItem?.id,
    updateOriginalLineItem: false,
  };
};
